import { AccountPanel } from '@/service';

/**
 * @class @name Panel
 * @description Exposes menu store module
 */
export default class Panel {
	/**
	 * @method @static @get @name namespaced
	 * @description Is this module namespaced?
	 */
	static get namespaced() {
		return true;
	}

	/**
	 * @method @static @get @name state
	 * @description Expose state as an object, compatible with vue stores
	 */
	static get state() {
		return {
			panelList: undefined
		};
	}

	/**
	 * @method @static @get @name getters
	 * @description Expose getters as an object, compatible with vue stores
	 */
	static get getters() {
		return {
			/**
			 * @name panelById
			 * @description used to get panel by ID from store
			 * @param {Object} state
			 */
			panelById: (state) => (id) => state.panelList.find((t) => t.id === id)
		};
	}

	/**
	 * @method @static @get @name mutations
	 * @description Expose mutations as an object, compatible with vue stores
	 */
	static get mutations() {
		return {
			/**
			 * @name setPanelList
			 * @param {Object} state
			 * @param {Array} data
			 */
			setPanelList(state, data) {
				state.panelList = data;
			},

			/**
			 * @name setPanel
			 * @param {Object} state
			 * @param {Object} vars
			 */
			setPanel(state, vars) {
				const { data, id } = vars;
				if (!state.panelList) state.panelList = [data];
				else {
					const arr = [...state.panelList];
					const idx = arr.reduce((p, c, i) => (c.id === id ? i : p), -1);
					if (idx >= 0) arr[idx] = { ...arr[idx], ...data };
					else arr.push(data);
					state.panelList = arr;
				}
			}
		};
	}

	/**
	 * @method @static @get @name actions
	 * @description Expose action as an object, compatible with vue stores
	 */
	static get actions() {
		return {
			/**
			 * @async @name loadPanelList
			 * @param {function} commit
			 */
			async loadPanelList({ commit }) {
				return AccountPanel.list()
					.then(({ data }) => {
						commit('setPanelList', data.data);
						return data;
					})
					.catch((error) => {
						throw error;
					});
			},

			/**
			 * @async @name modifyPanel
			 * @param {function} commit
			 */
			modifyPanel({ commit }, { id, payload }) {
				return AccountPanel.patch(id, payload)
					.then(({ data }) => {
						commit('setPanel', { data, id });
						return data;
					})
					.catch((error) => {
						throw error;
					});
			},

			/**
			 * @async @name addPanel
			 * @param {function} commit
			 */
			async addPanel({ commit }, payload) {
				return AccountPanel.post(payload)
					.then(({ data }) => {
						commit('setPanel', { data });
						return data;
					})
					.catch((error) => {
						throw error;
					});
			},

			/**
			 * @async @name deletePanel
			 * @param {function} commit
			 */
			async deletePanel({ commit, state }, id) {
				return AccountPanel.delete(id)
					.then(() => commit('setPanelList', [...state.panelList.filter((u) => u.id !== id)]))
					.catch((error) => {
						throw error;
					});
			},

			/**
			 * @async @name setDefaultPanelForUser
			 * @description Set the default panel for a user
			 */
			async setDefaultPanelForUser({ state, dispatch, rootState, rootGetters }) {
				if (!rootGetters['Account/shouldSetDefaultPanelForUser']) return null;

				if (!state.panelList) {
					await dispatch('loadPanelList');
				}

				const defaultPanel = state.panelList?.find((panel) => panel.isDefault);

				if (!defaultPanel) return null;

				const user = rootState.Account.user;

				if (user.panel === defaultPanel.id) return null;

				await dispatch('Account/modifyUser', { data: { panel: defaultPanel.id } }, { root: true });

				return defaultPanel;
			}
		};
	}
}
